/**
 * Deep links `t.me/pass24app_bot?startapp=<param>` (roadmap P0-3, P1-6). Telegram puts the parameter into the
 * signed initData as `start_param`; it is read from there, never from initDataUnsafe. It only picks the first
 * screen — access is still decided by mobile-api with the resident's token.
 */
export type DeepLink =
  | { kind: 'pass'; id: number }
  | { kind: 'request'; id: number }
  | { kind: 'object'; id: number }
  | { kind: 'news'; id: number };

const PATTERN = /^(pass|request|msg|obj|news)_(\d{1,10})$/;

export function parseStartParam(value: string | null | undefined): DeepLink | null {
  const m = value ? PATTERN.exec(value) : null;
  if (!m) return null;
  const id = Number(m[2]);
  if (!id) return null;
  if (m[1] === 'pass') return { kind: 'pass', id };
  if (m[1] === 'obj') return { kind: 'object', id };
  // A broadcast of the managing company (v1 TenantMessageNotification, #3698): the notifications list.
  if (m[1] === 'news') return { kind: 'news', id };
  return { kind: 'request', id }; // request_ and msg_ both open the conversation
}

export function deepLinkFromInitData(initData: string): DeepLink | null {
  return parseStartParam(new URLSearchParams(initData).get('start_param'));
}

/** Route to open once the resident is inside an object; object links are handled by the object gate. */
export function deepLinkRoute(link: DeepLink | null): string | null {
  if (link?.kind === 'pass') return `/passes/${link.id}`;
  if (link?.kind === 'request') return `/requests/${link.id}`;
  if (link?.kind === 'news') return '/notifications';
  return null;
}

/** The bot of this build: the /dev build is a separate mini app of the same bot. */
export function botLink(base: string = import.meta.env.BASE_URL): string {
  return base.startsWith('/dev') ? 'https://t.me/pass24app_bot/dev' : 'https://t.me/pass24app_bot';
}

/**
 * «Пригласить семью и соседей» (ideas 2026-10-13, №7): the посёлок link of the resident's own object, so the invited
 * person lands in the same object or straight in its join request (roadmap P1-6).
 */
export function appInviteLink(objectId: number, base: string = import.meta.env.BASE_URL): string {
  return `${botLink(base)}?startapp=obj_${objectId}`;
}

/** The Max bot of the mini app (build variable VITE_MAX_BOT): one bot for both builds, Max allows one app per bot. */
export function maxBotLink(bot: string | undefined = import.meta.env.VITE_MAX_BOT): string {
  return `https://max.ru/${bot ?? ''}`;
}

export function appInviteMessage(objectName: string, messenger = 'Telegram'): string {
  return `Заказываю пропуска в ${objectName} через ${messenger} — без пароля и SMS, гостю за 10 секунд. Присоединяйся: ссылка откроет наш посёлок.`;
}
